const DEFAULT_NUMBER_RANGE = {
  minNum: 0,
  maxNum: 10,
};

const getNumberRangeConfig = (question) => {
  try {
    const parsedConfig = JSON.parse(question?.other_column_data);
    return {
      minNum:
        typeof parsedConfig?.minNum === "number"
          ? parsedConfig.minNum
          : DEFAULT_NUMBER_RANGE.minNum,
      maxNum:
        typeof parsedConfig?.maxNum === "number"
          ? parsedConfig.maxNum
          : DEFAULT_NUMBER_RANGE.maxNum,
    };
  } catch (error) {
    return DEFAULT_NUMBER_RANGE;
  }
};

const hasPossibleInRangeCompletion = (rawValue, minNum, maxNum) => {
  if (!/^-?\d+$/.test(rawValue)) {
    return false;
  }

  const parsedMin = Math.ceil(minNum);
  const parsedMax = Math.floor(maxNum);

  if (parsedMin > parsedMax) {
    return false;
  }

  if (rawValue.startsWith("-")) {
    const absolutePrefix = rawValue.slice(1);

    if (!absolutePrefix) {
      return parsedMin < 0;
    }

    const maxDigits = String(Math.abs(parsedMin)).length;
    for (
      let digitCount = absolutePrefix.length;
      digitCount <= maxDigits;
      digitCount += 1
    ) {
      const multiplier = 10 ** (digitCount - absolutePrefix.length);
      const prefixValue = Number(absolutePrefix);
      const prefixMin = -1 * (prefixValue * multiplier + (multiplier - 1));
      const prefixMax = -1 * prefixValue * multiplier;

      if (prefixMax >= parsedMin && prefixMin <= parsedMax) {
        return true;
      }
    }

    return false;
  }

  const maxDigits = String(parsedMax).length;
  for (
    let digitCount = rawValue.length;
    digitCount <= maxDigits;
    digitCount += 1
  ) {
    const multiplier = 10 ** (digitCount - rawValue.length);
    const prefixValue = Number(rawValue);
    const prefixMin = prefixValue * multiplier;
    const prefixMax = prefixMin + (multiplier - 1);

    if (prefixMax >= parsedMin && prefixMin <= parsedMax) {
      return true;
    }
  }

  return false;
};

const NumberRangeInput = ({ question, className, onChange, onBlur, value }) => {
  const { minNum, maxNum } = getNumberRangeConfig(question);
  const inputName = `question-${question.id}`;

  const forwardChange = (nextValue) => {
    onChange({
      target: {
        name: inputName,
        value: nextValue,
      },
    });
  };

  const handleInputChange = (e) => {
    const nextValue = e.target.value;

    if (nextValue === "") {
      forwardChange("");
      return;
    }

    if (nextValue === "-" && minNum < 0) {
      forwardChange(nextValue);
      return;
    }

    if (!/^-?\d+$/.test(nextValue)) {
      return;
    }

    const parsedValue = Number(nextValue);
    const isValueInRange = parsedValue >= minNum && parsedValue <= maxNum;

    if (isValueInRange || hasPossibleInRangeCompletion(nextValue, minNum, maxNum)) {
      forwardChange(nextValue);
    }
  };

  const handleInputBlur = (e) => {
    const rawValue = e.target.value;

    if (rawValue === "" || rawValue === "-") {
      forwardChange("");
      onBlur?.(e);
      return;
    }

    const parsedValue = Number(rawValue);

    if (Number.isNaN(parsedValue)) {
      forwardChange("");
      onBlur?.(e);
      return;
    }

    if (parsedValue < minNum) {
      forwardChange(String(minNum));
    } else if (parsedValue > maxNum) {
      forwardChange(String(maxNum));
    }

    onBlur?.(e);
  };

  return (
    <>
      <p>{question.question_help}</p>
      <input
        key={inputName}
        name={inputName}
        className={className}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        value={value}
        type="number"
        min={minNum}
        max={maxNum}
        step="1"
        placeholder="Please enter a number"
      />
    </>
  );
};

export default NumberRangeInput;
